import { Injectable, inject } from '@angular/core'; // Trigger recompile
import { Firestore, collection, addDoc, deleteDoc, updateDoc, doc, query, getDocs, Timestamp, where, arrayUnion, getDoc, writeBatch } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

export interface Trip {
    id?: string;
    name: string;
    description?: string;
    startDate: string; // ISO Date String
    endDate?: string;   // ISO Date String
    status: 'active' | 'completed';
    createdBy: string;
    createdAt?: any;
    members?: string[];
    /** Non-registered participants, keyed by name in expense splits. */
    ghostMembers?: { id: string; name: string }[];
    /** Sum of the trip's expense amounts, maintained server-side (functions/src/trip-totals.ts). */
    totalSpent?: number;
}

@Injectable({
    providedIn: 'root'
})
export class TripService {
    private firestore: Firestore = inject(Firestore);
    authService = inject(AuthService); // Public for access in methods
    userService = inject(UserService); // Inject UserService

    private tripsSubject = new BehaviorSubject<Trip[]>([]);
    trips$ = this.tripsSubject.asObservable();

    isLoading = false;
    error: string | null = null;

    constructor() {
        this.authService.user$.subscribe(user => {
            if (user) {
                this.fetchTrips();
            } else {
                this.tripsSubject.next([]);
            }
        });
    }

    async fetchTrips() {
        if (!this.authService.getCurrentUser()) return;

        this.isLoading = true;
        this.error = null;
        try {
            const tripsCollection = collection(this.firestore, 'trips');

            // Query trips where the current user is a MEMBER
            const q = query(tripsCollection, where('members', 'array-contains', this.authService.getCurrentUser()?.uid));

            const snapshot = await getDocs(q);
            const trips = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trip));

            // Sort by date (newest first)
            trips.sort((a, b) => {
                const tA = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
                const tB = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
                return tB - tA;
            });

            this.tripsSubject.next(trips);
        } catch (err: any) {
            console.error('Error fetching trips:', err);
            this.error = `Could not load trips: ${err.message}`;
        } finally {
            this.isLoading = false;
        }
    }

    async addTrip(trip: Omit<Trip, 'id' | 'createdAt' | 'createdBy' | 'status'>) {
        const user = this.authService.getCurrentUser();
        if (!user) return;

        try {
            await addDoc(collection(this.firestore, 'trips'), {
                ...trip,
                createdBy: user.uid,
                status: 'active',
                members: [user.uid],
                createdAt: Timestamp.now()
            });
            await this.fetchTrips();
        } catch (error) {
            console.error("Error adding trip: ", error);
            throw error;
        }
    }

    getTripSnapshot(id: string): Trip | undefined {
        return this.tripsSubject.value.find(t => t.id === id);
    }

    async deleteTrip(id: string) {
        // Warning: This should also delete sub-expenses or flag them.
        // For now, simple delete.
        await deleteDoc(doc(this.firestore, 'trips', id));
        await this.fetchTrips();
    }

    async updateTrip(id: string, data: Partial<Trip>) {
        const tripRef = doc(this.firestore, 'trips', id);
        await updateDoc(tripRef, data);
        await this.fetchTrips();
    }

    /**
     * Fetches a single trip by ID.
     * Checks local cache first, then Firestore.
     */
    async getTrip(id: string): Promise<Trip | undefined> {
        // 1. Check local state
        const cached = this.tripsSubject.value.find(t => t.id === id);
        if (cached) return cached;

        // 2. Fetch from Firestore
        try {
            const docRef = doc(this.firestore, 'trips', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as Trip;
            }
        } catch (err) {
            console.error(`Error getting trip ${id}:`, err);
        }
        return undefined;
    }

    async addMemberByEmail(tripId: string, email: string) {
        const userToAdd = await this.userService.lookupUserByEmail(email);
        if (!userToAdd) throw new Error('User not found. They must sign up first.');

        const tripRef = doc(this.firestore, 'trips', tripId);
        await updateDoc(tripRef, {
            members: arrayUnion(userToAdd.uid)
        });
        await this.fetchTrips(); // Refresh to see update
    }

    async removeMember(tripId: string, uidToRemove: string) {
        // 1. Validation
        const trip = this.tripsSubject.value.find(t => t.id === tripId);
        if (!trip) return;
        if (trip.createdBy === uidToRemove) throw new Error('Cannot remove trip owner.');

        // 2. Fetch and Update Expenses
        const expensesCollection = collection(this.firestore, 'expenses');
        // Query expenses for this trip
        const q = query(expensesCollection, where('contextId', '==', tripId));
        const snapshot = await getDocs(q);

        const batch = writeBatch(this.firestore);
        let updateCount = 0;

        snapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            const expenseId = docSnap.id;
            let needsUpdate = false;
            let updates: any = {};

            // A. Check Shared By
            // Handle legacy data where sharedBy might be missing (implies 'Everyone')
            const currentSharedBy = data['sharedBy'] || trip.members || [];

            if (Array.isArray(currentSharedBy) && currentSharedBy.includes(uidToRemove)) {
                const newSharedBy = currentSharedBy.filter((uid: string) => uid !== uidToRemove);
                updates['sharedBy'] = newSharedBy;
                needsUpdate = true;

                // B. Recalculate Split (Only for Equal Splits)
                // If it was 'equal' or undefined (default), we redistribute.
                // If it was 'percentage' or 'self', we might break numbers, but requirement specifically asks for auto-adjust "trip split happen between all... equally".
                if (!data['splitType'] || data['splitType'] === 'equal') {
                    const count = newSharedBy.length;
                    if (count > 0) {
                        const share = parseFloat((100 / count).toFixed(2));
                        const newSplitDetails: any = {};
                        let runningTotal = 0;

                        newSharedBy.forEach((uid: string, index: number) => {
                            if (index === count - 1) {
                                newSplitDetails[uid] = parseFloat((100 - runningTotal).toFixed(2));
                            } else {
                                newSplitDetails[uid] = share;
                                runningTotal += share;
                            }
                        });
                        updates['splitDetails'] = newSplitDetails;
                    } else {
                        // Edge case: No one left sharing? Default to Payer or empty?
                        // Let's leave splitDetails empty
                        updates['splitDetails'] = {};
                    }
                } else if (data['splitType'] === 'percentage') {
                    // For percentage, just remove the user from details. 
                    // Percentages won't sum to 100 anymore, but we shouldn't guess how to redistribute percentages.
                    // The user prompt was specific about "trip split happen between all of them equally".
                    if (data['splitDetails']) {
                        const newDetails = { ...data['splitDetails'] };
                        delete newDetails[uidToRemove];
                        updates['splitDetails'] = newDetails;
                    }
                }
            }

            if (needsUpdate) {
                batch.update(doc(this.firestore, 'expenses', expenseId), updates);
                updateCount++;
            }
        });

        if (updateCount > 0) {
            await batch.commit();
        }

        // 3. Update Trip Members
        const updatedMembers = trip.members?.filter(m => m !== uidToRemove) || [];
        const tripRef = doc(this.firestore, 'trips', tripId);
        await updateDoc(tripRef, {
            members: updatedMembers
        });

        await this.fetchTrips();
    }
}
